import { Box, FormControl, FormControlProps, FormHelperText, useTheme } from '@mui/material'
import {
  FocusEvent,
  memo,
  ReactNode,
  Ref,
  useCallback,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  GroupBase,
  mergeStyles,
  Props as DefaultProps,
  SelectInstance,
  Theme as ReactSelectTheme,
  useStateManager,
} from 'react-select'
import Select from 'react-select/base'

import { MyInputLabel } from '../MyInputs/MyInputLabel'
import { MuiSelectSize, useMuiSelectStyles } from '../MySelect/selectStyles'
import FormSelectDetails from './FormSelectDetails'

/** Campo del panel de detalles de una opción seleccionada. */
export type DetailConfig<Option> = {
  /** Nombre del campo que se muestra a la izquierda. */
  label: string
  /** Propiedad de la opción o función que devuelve el contenido del campo. */
  accessor: keyof Option | ((option: Option) => ReactNode)
}

/** Props de react-select más la integración visual y los detalles de FormSelect. */
export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = DefaultProps<Option, IsMulti, Group> & {
  /** Marca el campo como inválido. Si se omite, usa formControlProps.error o false. */
  error?: boolean
  /** Texto de ayuda o error que aparece debajo del selector. */
  formHelperText?: ReactNode
  /** Etiqueta visible del campo. No es necesario agregar el asterisco de required. */
  inputLabel?: string
  /** Configuración del FormControl de MUI que contiene el campo. */
  formControlProps?: FormControlProps
  /** Tamaño del selector: small (predeterminado), medium o large. */
  size?: MuiSelectSize
  /** Ocupa todo el ancho disponible. Predeterminado: true. */
  fullWidth?: boolean
  /** Habilita el botón de detalles cuando hay selección. Predeterminado: false. */
  showDetails?: boolean
  /** Omite el botón de detalles al navegar con Tab; permite clic. Predeterminado: false. */
  disableDetailsTab?: boolean
  /** Campos adicionales por opción. Una lista no vacía también habilita los detalles. */
  detailsConfig?: DetailConfig<Option>[]
  /** Referencia a react-select para focus(), blur() o integración con field.ref. */
  ref?: Ref<SelectInstance<Option, IsMulti, Group>>
}

const defaultOptionLabel = <Option,>(option: Option) => String((option as { label?: unknown }).label ?? '')
const defaultOptionValue = <Option,>(option: Option) => String((option as { value?: unknown }).value ?? '')

const FormSelectComponent = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group>,
) => {
  const theme = useTheme()
  const {
    error: errorProp,
    required: requiredProp,
    isDisabled: disabledProp,
    formHelperText,
    inputLabel,
    formControlProps,
    size = 'small',
    onFocus,
    onBlur,
    fullWidth = true,
    placeholder = '-- Seleccione --',
    detailsConfig,
    showDetails = false,
    disableDetailsTab = true,
    styles,
    theme: externalTheme,
    menuPosition = 'fixed',
    getOptionLabel = defaultOptionLabel<Option>,
    getOptionValue = defaultOptionValue<Option>,
    ref,
    ...others
  } = props
  const error = errorProp ?? formControlProps?.error ?? false
  const required = requiredProp ?? formControlProps?.required ?? false
  const isDisabled = disabledProp ?? formControlProps?.disabled ?? false
  const uniqueId = useId()
  const selectInputId = props.inputId ?? `mui-select-input-${uniqueId}`
  const labelId = `mui-select-label-${uniqueId}`
  const helperId = `mui-select-helper-${uniqueId}`
  const hasHelper = formHelperText !== undefined && formHelperText !== null && formHelperText !== ''
  const [isFocused, setIsFocused] = useState(false)
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null)
  const [controlHeight, setControlHeight] = useState<number>()
  const detailsEnabled = showDetails || Boolean(detailsConfig?.length)
  useImperativeHandle(ref, () => selectRef.current!, [])

  // Mide el control visible, no su contenedor ni la altura del texto de ayuda.
  // También responde a estilos personalizados y chips que ocupan varias líneas.
  useLayoutEffect(() => {
    const control = selectRef.current?.controlRef
    if (!detailsEnabled || !control) return
    const updateHeight = () => setControlHeight(control.getBoundingClientRect().height)
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(control)
    return () => observer.disconnect()
  }, [detailsEnabled, props.components?.Control])

  // Comparte el estado oficial de react-select con el botón externo, incluso con defaultValue.
  const selectProps = useStateManager(others)
  const selectedValues: readonly Option[] = Array.isArray(selectProps.value)
    ? selectProps.value
    : selectProps.value == null
      ? []
      : [selectProps.value as Option]

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(event)
    },
    [onFocus],
  )
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      onBlur?.(event)
    },
    [onBlur],
  )

  const selectTheme = useMemo(
    () => (base: ReactSelectTheme) => {
      const muiTheme = { ...base, colors: { ...base.colors, primary: theme.palette.primary.main } }
      return typeof externalTheme === 'function' ? externalTheme(muiTheme) : (externalTheme ?? muiTheme)
    },
    [theme.palette.primary.main, externalTheme],
  )
  const muiStyles = useMuiSelectStyles<Option, IsMulti, Group>(error, size)
  const mergedStyles = useMemo(() => mergeStyles(muiStyles, styles), [muiStyles, styles])

  return (
    <FormControl
      {...formControlProps}
      error={error}
      required={required}
      disabled={isDisabled}
      fullWidth={fullWidth}
      variant="outlined"
      focused={isFocused}
      size={size === 'large' ? 'medium' : size}
      role="group"
      aria-labelledby={inputLabel ? labelId : undefined}
      aria-describedby={hasHelper ? helperId : undefined}
      sx={[
        { minWidth: 0, flex: 1 },
        ...(Array.isArray(formControlProps?.sx) ? formControlProps.sx : [formControlProps?.sx ?? {}]),
      ]}
    >
      {inputLabel && (
        <MyInputLabel
          id={labelId}
          htmlFor={selectInputId}
          shrink
          focused={isFocused}
          error={error}
          required={required}
        >
          {inputLabel}
        </MyInputLabel>
      )}
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <Select<Option, IsMulti, Group>
            {...selectProps}
            ref={selectRef}
            inputId={selectInputId}
            instanceId={props.instanceId ?? uniqueId}
            placeholder={placeholder}
            required={required}
            isDisabled={isDisabled}
            aria-invalid={props['aria-invalid'] ?? error}
            aria-errormessage={props['aria-errormessage'] ?? (error && hasHelper ? helperId : undefined)}
            styles={mergedStyles}
            onFocus={handleFocus}
            onBlur={handleBlur}
            theme={selectTheme}
            menuPosition={menuPosition}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
          />
        </Box>
        {(showDetails || Boolean(detailsConfig?.length)) && selectedValues.length > 0 && (
          <FormSelectDetails
            values={selectedValues}
            detailsConfig={detailsConfig}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            inputLabel={inputLabel}
            disableTab={disableDetailsTab}
            controlHeight={controlHeight}
          />
        )}
      </Box>
      {hasHelper && (
        <FormHelperText id={helperId} error={error} sx={{ mx: 0, mt: 0.5 }}>
          {formHelperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

/**
 * Selector genérico basado en react-select, con estilos MUI y panel de detalles.
 *
 * Admite selección simple y múltiple, opciones agrupadas y los modos controlado
 * (`value` y `onChange`) y no controlado (`defaultValue`). Por defecto, las opciones
 * usan las propiedades `label` y `value`; para otros modelos, proporciona
 * `getOptionLabel` y `getOptionValue` con un identificador único por opción.
 *
 * En selección simple, limpiar devuelve `null`. En múltiple, devuelve un arreglo
 * vacío; usa `isMulti` y el segundo genérico `true` para tipar el arreglo readonly.
 * Los detalles muestran la selección actual y desaparecen cuando está vacía.
 * Enter o Espacio abren el panel desde el botón; Escape o clic fuera lo cierran.
 *
 * @example Selección simple controlada
 * ```tsx
 * import { useState } from 'react'
 * import FormSelect from './FormSelect'
 *
 * type Almacen = { codigo: string; nombre: string; direccion: string }
 * const almacenes: Almacen[] = [
 *   { codigo: 'A1', nombre: 'Central', direccion: 'Av. Principal 123' },
 *   { codigo: 'A2', nombre: 'Norte', direccion: 'Calle Norte 456' },
 * ]
 *
 * function SeleccionSimple() {
 *   const [almacen, setAlmacen] = useState<Almacen | null>(null)
 *
 *   return (
 *     <FormSelect<Almacen>
 *       inputLabel="Almacén"
 *       options={almacenes}
 *       value={almacen}
 *       onChange={setAlmacen}
 *       getOptionLabel={(opcion) => opcion.nombre}
 *       getOptionValue={(opcion) => opcion.codigo}
 *       isClearable
 *       required
 *       detailsConfig={[{ label: 'Dirección', accessor: 'direccion' }]}
 *     />
 *   )
 * }
 * ```
 *
 * @example Selección múltiple controlada
 * ```tsx
 * import { useState } from 'react'
 * import FormSelect from './FormSelect'
 *
 * type Opcion = { value: string; label: string }
 * const opciones: Opcion[] = [
 *   { value: 'A1', label: 'Central' },
 *   { value: 'A2', label: 'Norte' },
 * ]
 *
 * function SeleccionMultiple() {
 *   const [seleccionados, setSeleccionados] = useState<readonly Opcion[]>([])
 *
 *   return (
 *     <FormSelect<Opcion, true>
 *       inputLabel="Almacenes"
 *       isMulti
 *       options={opciones}
 *       value={seleccionados}
 *       onChange={setSeleccionados}
 *       closeMenuOnSelect={false}
 *       isClearable
 *       showDetails
 *       disableDetailsTab
 *     />
 *   )
 * }
 * ```
 */
const FormSelect = memo(FormSelectComponent) as typeof FormSelectComponent
export default FormSelect
